<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core'
import { ActionFields } from '~/components/cms/Action/actionHelpers'
import { BREAKPOINTS } from '~/config/common'
import { GROUP_NAMES, trackClickedItem } from '~/helpers/datalayer/search-event'

type SearchProps = {
  iconClass: string
  searchPanelOpen: boolean
  popularCategories?: ActionFields[]
  searchProductRecommender?: {
    recommenderHeading: string
    recommenderID: string
    supportingData: {
      home?: {
        action?: {
          value?: 'addToBag' | undefined
        }
      }
    }
  }
}
type SearchResult = {
  searchSuggestions?: {
    url: string
    text: string
    totalResults?: number
  }[]
  products?: {
    image: {
      url: string
      alt: string
    }
    productId: string
    productName: string
    url: string
    tieredPrice: {
      amount: number
      type: string
      currency: string
      retailPrice: number
    }
    price: number
  }[]
  categories?: {
    url: string
    name: string
    parentCategory?: string
  }[]
  content?: {
    url: string
    name: string
  }[]
}

const emit = defineEmits(['openSearchPanel', 'closeSearchPanel'])
const props = defineProps<SearchProps>()
const { getPopularSearches, getSearchSuggestions } = useConstructor()
const { trackAutocompleteSelect, trackInputFocus, trackSearchSubmit } = useConstructorTracker()
const { attributeSearchClick } = useSearchAttribution()
const queryText = ref('')
const results = ref<SearchResult>({})
const popularSearchTerms = ref<any[]>()
const { locale } = useI18n()
const searchBar = ref<HTMLInputElement | null>(null)
const { add: addRecentSearch } = useRecentSearches()
const { getLocalePrefix } = useSiteUrl()
provide('location', LOCATIONS.header)

const fetchResults = useDebounceFn(async (newQuery: string) => {
  if (newQuery.length > 2) {
    results.value = await getSearchSuggestions(newQuery)
  }
}, 500)

const focusSearchBar = () => {
  searchBar?.value?.focus()
}

const handleTouchOutsideInputBox = (event: Event) => {
  // @ts-ignore
  if (event?.target?.id !== 'search-bar') {
    searchBar?.value?.blur() // Blur Focus from Input to hide the Keyboard on mobile
  }
}

const clearSearchBar = () => {
  queryText.value = ''
  focusSearchBar()
}

const showSearch = async () => {
  if (!queryText.value) return
  pushEventToDataLayer(
    'custom.search.submit',
    {
      group: 'suggested search',
      type: 'enter press',
      search: {
        term: queryText.value,
      },
    },
    locale.value,
  )
  trackSearchSubmit(queryText.value, { originalQuery: queryText.value })

  // TODO: Use this when search page is available in Next Gen
  // useRouter().push(`${getLocalePrefix(locale.value)}/search?q=${queryText.value}`)
  const term = queryText.value
  addRecentSearch(term)
  // useCookie writes on the next tick; the results page reads the history from the request's cookie.
  await nextTick()
  window.location.href = `${getLocalePrefix()}/search?q=${term}`
  queryText.value = ''
  emit('closeSearchPanel')
}

const hasSearchResults = computed(
  () =>
    queryText.value &&
    (results.value.categories?.length || results.value.products?.length || results.value.content?.length),
)

const openModal = () => {
  emit('openSearchPanel')
  setTimeout(() => {
    focusSearchBar()
  }, 100)
  trackResultViewUpdate()
}

const createSuggestedSearchGroup = (labelKey: string, type: string, data: any[]) => ({
  type,
  resultCount: data.length,
  results: data.map((item, index) => ({
    label: item[labelKey],
    position: index + 1,
    url: item.url,
  })),
})

const getSuggestedSearchGroupsData = () => {
  const suggestedSearchGroups = []

  if (popularSearchTerms.value && !queryText.value) {
    suggestedSearchGroups.push(
      createSuggestedSearchGroup('text', GROUP_NAMES.POPULAR_SEARCHES, popularSearchTerms.value),
    )
  }

  if (props.popularCategories && props.popularCategories.length > 0) {
    const mappedCategories = props.popularCategories.map((item) => ({
      text: item.fields?.text,
      url: item.fields?.url,
    }))
    suggestedSearchGroups.push(createSuggestedSearchGroup('text', GROUP_NAMES.POPULAR_CATEGORIES, mappedCategories))
  }

  if (results.value.searchSuggestions && results.value.searchSuggestions.length > 0) {
    suggestedSearchGroups.push(
      createSuggestedSearchGroup('text', GROUP_NAMES.SUGGESTED_SEARCHES, results.value.searchSuggestions),
    )
  }

  if (results.value.categories && results.value.categories.length > 0) {
    suggestedSearchGroups.push(
      createSuggestedSearchGroup('parentCategory', GROUP_NAMES.RELATED_CATEGORIES, results.value.categories),
    )
  }

  if (results.value.products && results.value.products.length > 0) {
    suggestedSearchGroups.push(
      createSuggestedSearchGroup('productName', GROUP_NAMES.RELATED_PRODUCTS, results.value.products),
    )
  }

  if (results.value.content && results.value.content.length > 0) {
    suggestedSearchGroups.push(createSuggestedSearchGroup('name', GROUP_NAMES.RELATED_CONTENT, results.value.content))
  }

  return suggestedSearchGroups
}

const trackSearchResultsEvent = (
  eventName: DataLayerEventName,
  searchTerm?: string,
  clickedItem?: any,
  index?: number,
  groupType?: string,
) => {
  const resultGroups = getSuggestedSearchGroupsData()
  const resultGroupsTotalResults = resultGroups.reduce((currentTotal, arrayItem) => {
    return currentTotal + arrayItem.resultCount
  }, 0)

  const trackingData = {
    group: 'suggested search',
    category: 'search',
    label: searchTerm || undefined,
    search: {
      term: searchTerm || undefined,
      resultCount: resultGroupsTotalResults,
      resultGroups,
      clickedResult: {},
    },
    info: {
      location: 'site header',
    },
  }

  if (clickedItem) {
    trackingData.search.clickedResult = trackClickedItem(clickedItem.text, clickedItem.url, index, groupType)
  }
  // SAFETY: search.term and resultGroups are narrower in DataLayerOptional than what the panel can produce
  // (an empty term, more than one group); the data layer accepts both.
  pushEventToDataLayer(eventName, trackingData as DataLayerOptional, locale.value)
}

const trackResultClick = (clickedItem: any, index: number, groupType: string) => {
  // Picking a product straight out of autocomplete is the same journey as clicking a search result.
  if (groupType === GROUP_NAMES.RELATED_PRODUCTS) {
    attributeSearchClick(queryText.value, [results.value.products?.[index]?.productId])
    trackAutocompleteSelect(clickedItem.name, {
      originalQuery: queryText.value,
      tr: 'click',
      section: 'Products',
      itemId: results.value.products?.[index]?.productId,
    })
  } else if (groupType === GROUP_NAMES.RELATED_CONTENT) {
    trackAutocompleteSelect(clickedItem.name, {
      originalQuery: queryText.value,
      tr: 'click',
      section: 'Content',
      itemId: results.value.content?.[index]?.name,
    })
  } else if (groupType === GROUP_NAMES.SUGGESTED_SEARCHES) {
    trackAutocompleteSelect(clickedItem.name, {
      originalQuery: queryText.value,
      tr: 'click',
      section: 'Search Suggestions',
      itemId: results.value.searchSuggestions?.[index]?.text,
    })
  } else if (groupType === GROUP_NAMES.RELATED_CATEGORIES) {
    trackAutocompleteSelect(clickedItem.name, {
      originalQuery: queryText.value,
      tr: 'click',
      section: 'Categories',
      groupId: clickedItem.text,
      displayName: clickedItem.name,
    })
  }
  return trackSearchResultsEvent('custom.search.results.click', queryText.value, clickedItem, index, groupType)
}

const trackResultViewUpdate = (searchTerm?: string) => {
  return trackSearchResultsEvent('custom.search.results.view', searchTerm)
}

const debouncedTrackResultViewUpdate = useDebounceFn((newQueryText: string) => {
  trackResultViewUpdate(newQueryText)
}, 2000)

onMounted(async () => {
  popularSearchTerms.value = await getPopularSearches()
})

watch(queryText, (newQueryText: string) => {
  fetchResults(newQueryText)
  debouncedTrackResultViewUpdate(newQueryText)
})

const swiperOptions = {
  breakpoints: {
    [BREAKPOINTS.mobile]: { slidesPerView: 2, slidesPerGroup: 2 },
    [BREAKPOINTS.desktop]: { slidesPerView: 3, slidesPerGroup: 3 },
  },
}

const hasRecentProducts = ref(true)
const onRecentProductsLoad = (productsCount: number) => {
  hasRecentProducts.value = productsCount > 0
}
</script>

<template>
  <div
    class="search"
    :class="{ 'search--open': searchPanelOpen }"
    @keyup.escape="$emit('closeSearchPanel')"
    @touchstart="handleTouchOutsideInputBox"
  >
    <button
      :aria-label="$t('search_page.button_go_text')"
      :class="iconClass"
      data-testid="header-search"
      @click="openModal"
    >
      <svg-icon name="mhj:search" />
    </button>
    <div class="search__mask" @click="$emit('closeSearchPanel')"></div>

    <section class="search__panel" :class="{ 'search__panel--has-results': hasSearchResults }">
      <div class="search__container">
        <div class="search__bar">
          <div class="search__close-panel">
            <button
              class="search__close-button"
              :aria-label="$t('screen_reader.buttons.close')"
              data-testid="search-close"
              @click="$emit('closeSearchPanel')"
            >
              <svg-icon name="mhj:cross" />
            </button>
          </div>

          <div class="search__input-wrap">
            <input
              ref="searchBar"
              id="search-bar"
              type="search"
              data-testid="search-input"
              v-model="queryText"
              @keyup.enter="showSearch"
              @focus="trackInputFocus"
              :placeholder="$t('search_page.input_search_placeholder')"
            />
            <button class="search__bar-reset" @click="clearSearchBar" @keyup.enter="clearSearchBar" v-show="queryText">
              {{ $t('search_page.clear_input') }}
            </button>
          </div>
        </div>

        <div class="search__results">
          <template v-if="!queryText">
            <RecentSearches />

            <RecommendationRotator
              v-if="searchPanelOpen"
              class="search__result-section search__recent-products"
              recommenderID="search-recent-products"
              :supportingData="{ home: { action: { value: 'addToBag' } } }"
              :customSwiperOptions="swiperOptions"
              :image-sizes="productImageSizes.search"
              @load="onRecentProductsLoad"
            />

            <section
              v-if="popularSearchTerms && popularSearchTerms.length"
              class="search__result-section search__popular-searches"
            >
              <h5 class="search__result-section-heading">{{ $t('search_page.popular_searches') }}</h5>
              <ul>
                <li v-for="(item, index) in popularSearchTerms" :key="item.id">
                  <a :href="item.url" @click="trackResultClick(item, index as number, GROUP_NAMES.POPULAR_SEARCHES)">
                    {{ item.text }}
                  </a>
                </li>
              </ul>
            </section>

            <section
              v-if="popularCategories && popularCategories.length"
              class="search__result-section search__popular-searches"
            >
              <h5 class="search__result-section-heading">{{ $t('search_page.popular_categories') }}</h5>
              <ul>
                <template
                  v-for="(item, index) in popularCategories"
                  :key="index"
                  @click="trackResultClick(item.fields, index, GROUP_NAMES.POPULAR_CATEGORIES)"
                >
                  <li v-if="item.fields">
                    <Action v-bind="item" />
                  </li>
                </template>
              </ul>
            </section>

            <RecommendationRotator
              v-if="searchPanelOpen && searchProductRecommender && !hasRecentProducts"
              class="search__result-section search__recent-products"
              :recommenderHeading="searchProductRecommender.recommenderHeading"
              :recommenderID="searchProductRecommender.recommenderID"
              :supportingData="searchProductRecommender.supportingData"
              :customSwiperOptions="swiperOptions"
              :image-sizes="productImageSizes.search"
            />
          </template>
          <template v-else>
            <section
              v-if="results.searchSuggestions && results.searchSuggestions.length"
              class="search__result-section search__suggested-searches"
            >
              <h5 class="search__result-section-heading">{{ $t('search_page.suggested_searches') }}</h5>
              <ul>
                <li v-for="(suggestion, index) in results?.searchSuggestions.slice(0, 5)" :key="`suggestion-${index}`">
                  <a
                    :href="suggestion.url"
                    data-testid="search-suggestion"
                    @click="trackResultClick(suggestion, index, GROUP_NAMES.SUGGESTED_SEARCHES)"
                  >
                    {{ suggestion.text }}
                    <span v-if="suggestion.totalResults !== undefined" class="search__suggestion-count">
                      ({{ suggestion.totalResults }})
                    </span>
                  </a>
                </li>
              </ul>
            </section>

            <section
              v-if="results.categories && results.categories.length"
              class="search__result-section search__related_categories"
            >
              <h5 class="search__result-section-heading">{{ $t('search_page.related_categories') }}</h5>
              <ul>
                <li v-for="(category, index) in results?.categories" :key="`category-${index}`">
                  <a
                    :href="category.url"
                    @click="
                      trackResultClick(
                        {
                          ...category,
                          text: category.parentCategory
                            ? `${category.name} in ${category.parentCategory}`
                            : category.name,
                        },
                        index,
                        GROUP_NAMES.RELATED_CATEGORIES,
                      )
                    "
                  >
                    {{ category.name }}
                    <span v-if="category.parentCategory" class="search__category-parent">
                      in {{ category.parentCategory }}
                    </span>
                  </a>
                </li>
              </ul>
            </section>

            <section
              v-if="results.products && results.products.length"
              class="search__result-section search__related_products"
            >
              <h5 class="search__result-section-heading">{{ $t('search_page.related_products') }}</h5>
              <ul>
                <li
                  v-for="(product, index) in results?.products"
                  :key="`suggested-product-${index}`"
                  data-testid="search-suggested-product"
                  @click="
                    trackResultClick({ ...product, text: product.productName }, index, GROUP_NAMES.RELATED_PRODUCTS)
                  "
                >
                  <ProductCardSummary :product="product" />
                </li>
              </ul>
            </section>

            <section
              v-if="results.content && results.content.length"
              class="search__result-section search__related_content"
            >
              <h5 class="search__result-section-heading">{{ $t('search_page.related_content') }}</h5>
              <ul>
                <li v-for="(content, index) in results?.content" :key="`content-${index}`">
                  <a
                    :href="content.url"
                    @click="trackResultClick({ ...content, text: content.name }, index, GROUP_NAMES.RELATED_CONTENT)"
                  >
                    {{ content.name }}
                  </a>
                </li>
              </ul>
            </section>
          </template>
        </div>
      </div>
    </section>
  </div>
</template>

<style lang="postcss">
@import '~/assets/css/placeholders.css';

.search {
  --search-bar-height: 4.5rem;

  .search__mask {
    position: fixed;
    z-index: 0;
    inset: 0 0 0 0;
    height: 100vh;
    background: var(--mask-bg-dark);
    display: none;

    @media (--desktop) {
      inset: var(--header-height) 0 0 0;
    }
  }

  .search__panel {
    position: fixed;
    top: 0;
    right: 0;
    z-index: var(--z-header);
    width: 100%;
    max-width: 28rem;
    height: 100%;
    padding: var(--space-110) var(--space-50) var(--space-70);
    background-color: var(--surface-light-00);
    display: none;
    opacity: 0;
    animation: fade-out 0.3s cubic-bezier(0.25, 0, 0.3, 1);
  }

  .search__container {
    max-width: 1280px;
    margin: 0 auto;
  }

  &.search--open {
    .search__panel {
      opacity: 1;
      display: block;
      animation: fade-in 0.4s cubic-bezier(0.25, 0, 0.3, 1);
      overscroll-behavior: contain;
      overflow-y: auto;
    }

    .search__mask {
      display: block;
    }
  }

  .search__bar {
    margin-bottom: var(--space-60);

    .search__close-panel {
      height: 2rem;
      margin-bottom: var(--space-50);
      position: absolute;
      right: var(--space-50);
      top: var(--space-70);
    }

    .search__input-wrap {
      max-width: 78rem;
      position: relative;
      width: 100%;
      margin: 0 auto;

      input {
        border-radius: 0;
        border: 1px solid var(--border-neutral-40);
        min-height: 3rem; /* Match to PROD */
        padding: var(--space-30) var(--space-40);
        width: 100%;
        background: transparent;

        @media (--mobile-tablet-only) {
          font-size: var(--space-50); /* Avoid iOS zoom min font size */
        }

        &:focus-visible {
          border-color: var(--status-info);
          outline-style: inset;
        }
      }
    }

    .search__bar-reset {
      position: absolute;
      top: 50%;
      right: var(--space-40);
      transform: translateY(-50%);
      cursor: pointer;
      font-size: var(--text-small);
      text-decoration: underline;
      color: var(--text-primary);
    }
  }

  .search__results {
    max-width: 78rem;
    margin: 0 auto;
    max-height: 100vh;
    scrollbar-width: none;

    @media (--mobile-tablet-only) {
      padding-bottom: 9rem; /* compensate for fixed items in the top */
      min-height: 100vh;
    }

    &::-webkit-scrollbar {
      background: transparent;
      width: 0;
    }
  }

  .search__result-section {
    padding-inline: var(--space-50);
    padding-bottom: var(--space-70);
    border-bottom: 1px solid var(--border-neutral-20);
    margin-bottom: var(--space-70);

    &:last-child {
      border-bottom: none;
    }

    ul {
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: var(--space-60);
    }

    a {
      text-decoration: none;
    }

    .search__result-section-heading,
    .recommender__title {
      font-size: var(--text-small);
      color: var(--text-secondary);
      text-transform: uppercase;
      margin-bottom: var(--space-60);
    }

    .recommender__title {
      margin-bottom: 0;
    }

    &.search__recent-products {
      .recommender__top-section {
        align-items: flex-start;
      }
    }
  }

  .search__suggestion-count {
    color: var(--text-tertiary);
  }

  .search__related_categories {
    a {
      span {
        color: var(--text-secondary);
      }
    }
  }

  .search__related_products {
    ul {
      gap: var(--space-50);
      & .pdp-promotions__list {
        gap: 0;
      }
    }
  }

  @media (--desktop) {
    .search__panel {
      width: 100%;
      max-width: none;
      height: auto;
      max-height: 58rem; /* Match to PROD */
      top: var(--header-height);
      border-top: 0.4rem solid var(--surface-accent);
      padding-top: var(--space-70);
    }

    .search__container {
      height: 100%;
    }

    .search__panel--has-results {
      height: 90vh;

      .search__results {
        height: calc(100% - var(--search-bar-height));
      }
    }

    .search__bar {
      display: flex;
      flex-direction: row-reverse;
      align-items: flex-start;
      gap: var(--space-70);

      .search__close-panel {
        position: static;
        flex-shrink: 0;
        margin-bottom: 0;
      }

      .search__input-wrap {
        flex: 1;
        margin: 0;
      }
    }

    .search__results {
      display: flex;
      justify-content: space-between;
    }

    .search__result-section {
      width: 25%;
      max-width: 12.5rem;
      border: none;

      &.search__recent-products {
        width: 100%;
        max-width: 31.25rem; /* 500px */
        margin: 0;
      }

      &.search__related_products {
        min-width: 25.125rem; /* 402px */
      }
    }
  }

  /* Note 04/03/2025: Revisit the style once Vue3 migration is complete to match legacy design*/
  .product-card__name {
    font-size: var(--text-base);
    font-weight: normal;
    margin-bottom: var(--space-30);
  }
}
</style>
